import CampaignApi from "../api/CampaignApi";
import BaseUrl from "../types/BaseUrl";
import IArticleCampaign from "../types/IArticleCampaign";
import IArticleCampaigns from "../types/IArticleCampaigns";
import IArticleCampaignsV2 from "../types/IArticleCampaignsV2";
import IArticleCampaignV2 from "../types/IArticleCampaignV2";
import ICategoryCampaign from "../types/ICategoryCampaign";
import ICategoryCampaigns from "../types/ICategoryCampaigns";
import ICategoryCampaignsV2 from "../types/ICategoryCampaignsV2";
import ICategoryCampaignV2 from "../types/ICategoryCampaignV2";
import IMerchandisingSlots from "../types/IMerchandisingSlots";
import ISelectedArticles from "../types/ISelectedArticles";
import ISelectedExtras from "../types/ISelectedExtras";
import Options from "../types/Options";
import CampaignID from "./CampaignID";
import CampaignInfoStorageContainer from "./containers/localStorage";
import CampaignInfoNoStorageContainer from "./containers/noLocalStorageSupport";

export default class CampaignDatasource {
  private readonly merchandisingName = "merchandising";
  private campaignUUID: string;
  private campaignApi: CampaignApi;
  private container: CampaignInfoStorageContainer;
  private options: Options;

  constructor(baseUrl: BaseUrl, options: Options) {
    this.options = options;
    this.campaignUUID = new CampaignID(baseUrl, this.options).getUUID();

    this.campaignApi = new CampaignApi(baseUrl, this.options);
    this.container =
      typeof localStorage !== "undefined" ? new CampaignInfoStorageContainer() : new CampaignInfoNoStorageContainer();

    this.container.empty(this.campaignUUID);
  }

  public async getAllMerchandisingSlotsInfo(): Promise<IMerchandisingSlots> {
    let slots: IMerchandisingSlots = this.container.getSlots(this.campaignUUID);

    if (!Object.keys(slots).length) {
      const rawSlots = await this.campaignApi.getAllMerchandisingSlotsInfo(this.campaignUUID);

      let merchSlots: {};
      if (this.merchandisingShouldBeMerged(!!rawSlots.length)) {
        const rawMerchandisingSlots = await this.campaignApi.getAllMerchandisingSlotsInfo(this.merchandisingName);
        merchSlots = this.getItemsByIndex(rawMerchandisingSlots, false, (item) => item.slot_id.toString());
      }

      slots = {
        ...merchSlots,
        ...this.getItemsByIndex(rawSlots, this.merchandisingShouldBeMerged(!!rawSlots.length), (item) =>
          item.slot_id.toString()
        )
      };

      this.container.storeSlots(this.campaignUUID, slots);
    }

    return this.getFinalResult(slots);
  }

  public async getMerchandisingSlotInfo(slotId: string): Promise<ICategoryCampaign> {
    const info = await this.getAllMerchandisingSlotsInfo();
    return this.getFinalResult(info?.[slotId]);
  }

  public async getAllCategoriesCampaignInfo(): Promise<ICategoryCampaigns | ICategoryCampaignsV2> {
    let categories: ICategoryCampaigns | ICategoryCampaignsV2 = this.container.getCategories(this.campaignUUID);

    if (!Object.keys(categories).length) {
      const rawCategories = await this.campaignApi.getAllCategoriesCampaignInfo(this.campaignUUID);

      let merchandisingCategories: ICategoryCampaigns | ICategoryCampaignsV2 = {};
      if (this.merchandisingShouldBeMerged(!!rawCategories.length)) {
        const rawMerchandisingCategories = await this.campaignApi.getAllCategoriesCampaignInfo(this.merchandisingName);
        merchandisingCategories = this.getItemsByIndex(rawMerchandisingCategories, false, (item) =>
          item.product_category.toString()
        );
      }

      categories = {
        ...merchandisingCategories,
        ...this.getItemsByIndex(rawCategories, this.merchandisingShouldBeMerged(!!rawCategories.length), (item) =>
          item.product_category.toString()
        )
      };

      this.container.storeCategories(this.campaignUUID, categories);
    }

    return this.getFinalResult(categories);
  }

  public async getCategoryCampaignInfo(category: string): Promise<ICategoryCampaign | ICategoryCampaignV2> {
    const categoriesInfo = await this.getAllCategoriesCampaignInfo();

    return this.getFinalResult(categoriesInfo[category.toLowerCase()]);
  }

  public async getCategoryArticlesCampaignInfo(category: string): Promise<IArticleCampaigns | IArticleCampaignsV2> {
    let articles: IArticleCampaigns | IArticleCampaignsV2 = this.container.getArticles(this.campaignUUID, category);

    if (!Object.keys(articles).length) {
      const rawArticles = await this.campaignApi.getCategoryArticlesCampaignInfo(this.campaignUUID, category);

      let merchandisingArticles: IArticleCampaigns | IArticleCampaignsV2 = {};
      if (this.merchandisingShouldBeMerged(!!rawArticles.length)) {
        const rawMerchandisingArticles = await this.campaignApi.getCategoryArticlesCampaignInfo(
          this.merchandisingName,
          category
        );
        merchandisingArticles = this.getItemsByIndex(rawMerchandisingArticles, false, (item) =>
          item.product_id.toString()
        );
      }

      articles = {
        ...merchandisingArticles,
        ...this.getItemsByIndex(rawArticles, this.merchandisingShouldBeMerged(!!rawArticles.length), (item) =>
          item.product_id.toString()
        )
      };

      this.container.storeArticles(this.campaignUUID, category, articles);
    }

    return this.getFinalResult(articles);
  }

  public async getArticleCampaignInfo(category: string, papId: string): Promise<IArticleCampaign | IArticleCampaignV2> {
    const articlesInfo = await this.getCategoryArticlesCampaignInfo(category);

    return this.getFinalResult(articlesInfo[papId.toUpperCase()]);
  }

  public async getArticleWithExtrasCampaignInfo(
    category: string,
    papId: string,
    selectedExtras: ISelectedExtras
  ): Promise<IArticleCampaign | IArticleCampaignV2> {
    let articleInfo: any = await this.campaignApi.getArticleWithExtrasCampaignInfo(
      this.campaignUUID,
      category,
      papId,
      selectedExtras
    );

    const campaignHasOffer = this.isValidOffer(articleInfo);
    const campaignIsValid = Object.keys(articleInfo).length > 0;

    if (!campaignHasOffer && this.merchandisingShouldBeMerged(campaignIsValid)) {
      const merchandisingArticleInfo: any = await this.campaignApi.getArticleWithExtrasCampaignInfo(
        this.merchandisingName,
        category,
        papId,
        selectedExtras
      );
      articleInfo = merchandisingArticleInfo;
    }

    return this.getFinalResult(articleInfo);
  }

  public async getArticlesWithExtrasCampaignInfo(
    category: string,
    articles: ISelectedArticles
  ): Promise<IArticleCampaigns[] | IArticleCampaignsV2[]> {
    let articlesInfo: any = await this.campaignApi.getArticlesWithExtrasCampaignInfo(
      this.campaignUUID,
      category,
      articles
    );

    const invalidOffers = articlesInfo.reduce((indices: number[], articleInfo: any, index: number) => {
      const campaignHasOffer = this.isValidOffer(articleInfo);
      const campaignIsValid = Object.keys(articleInfo).length > 0;
      if (!campaignHasOffer && this.merchandisingShouldBeMerged(campaignIsValid)) indices.push(index);

      return indices;
    }, []);

    if (invalidOffers.length) {
      const merchandisingArticlesInfo: any = await this.campaignApi.getArticlesWithExtrasCampaignInfo(
        this.merchandisingName,
        category,
        articles
      );

      invalidOffers.forEach((index: number) => {
        articlesInfo[index] = merchandisingArticlesInfo[index];
      });
    }
    return articlesInfo.map((articleInfo: any) => this.getFinalResult(articleInfo));
  }

  private getFinalResult(items: any): any {
    if (!items || (typeof items === "object" && !Object.keys(items).length)) {
      return {};
    }

    return items;
  }

  getCampaignUUID(): string {
    return this.campaignUUID === this.merchandisingName ? "" : this.campaignUUID;
  }

  private merchandisingShouldBeMerged(isAValidCampaign: boolean = true) {
    return this.getCampaignUUID() !== "" && this.options.merchandisingShouldBeMerged(isAValidCampaign);
  }

  private getItemsByIndex(
    rawItems: any,
    excludeItemsWithoutOffer: boolean,
    getCollectionIndexKey: (item: any) => string
  ) {
    const items: any = [];

    rawItems.forEach((item: any) => {
      const isValidOffer = this.isValidOffer(item);
      const isValidSlot = !!item.slot_id;
      if (isValidOffer || isValidSlot || !excludeItemsWithoutOffer) {
        items[getCollectionIndexKey(item)] = item;

        if (getCollectionIndexKey(item) === "perspex") {
          items["plexiglas"] = JSON.parse(JSON.stringify(item).replace("perspex", "plexiglas"));
        }
      }
    });

    return items;
  }

  private isValidOffer(item: any) {
    const discountField = "discount_price";
    return this.options.getApiVersion() === 1
      ? !!item[discountField]
      : !!item[discountField] || item?.reference_highest_offer !== item?.reference_offer_percentage;
  }
}
